import HttpClient from '../httpClient';
import { TeamMember } from './teamController';

export interface UpdateJobPostingPayload {
  orgId: string;
  jobId?: string;
  [key: string]: unknown;
}

export interface CreateJobPostingPayload {
  orgId: string;
}

export type InterviewStatus =
  | 'not_started'
  | 'launching'
  | 'live'
  | 'submitted'
  | 'completed'
  | 'review_processing'
  | 'review_ready'
  | 'reviewed'
  | 'failed'
  | 'expired'
  | 'withdrawn'
  | 'abandoned';

export type InterviewVariant = 'full' | 'preview';

export const ROLE_EVIDENCE_SCORE_SCOPE =
  'Based on resume analysis and AI interview evidence against calibrated role requirements' as const;

export type RoleEvidenceRecommendation =
  | 'strong_advance'
  | 'advance'
  | 'hold_for_focused_human_review'
  | 'not_recommended_from_wisedoc_analysis'
  | 'not_decision_ready';

export type AssessmentStatus =
  | 'decision_ready'
  | 'decision_ready_with_caveats'
  | 'human_review_required'
  | 'not_decision_ready';

export type RecruiterRequirementAnswer =
  | 'confirmed'
  | 'confirmed_with_caveat'
  | 'conflict_found'
  | 'not_demonstrated_in_this_interview'
  | 'assessment_invalid';

export type EvidenceStrength = 'strong' | 'moderate' | 'thin' | 'not_reliable';

export type EvidenceAnchorStrength = 'strong' | 'moderate' | 'thin' | 'invalid';

export type EvidenceAnchorSource =
  | 'resume'
  | 'interview_transcript'
  | 'job_description'
  | 'hm_context'
  | 'system_event'
  | 'work_sample'
  | 'reference_note'
  | 'prior_interview_note';

export type RequirementImportance =
  | 'hm_confirmed_hard_must_have'
  | 'system_proposed_hard_must_have'
  | 'preferred'
  | 'rampable'
  | 'signal_only';

export type RequirementAssessmentMode =
  | 'must_probe_in_interview'
  | 'probe_if_time'
  | 'resume_disclosed_only'
  | 'excluded_from_scoring';

export interface AssessmentReadiness {
  value: number | null;
  status: AssessmentStatus;
  reasons: string[];
  affectedRequirementIds: string[];
}

export interface RoleEvidenceDecisionPayload {
  label: 'Role Evidence Score';
  scope: typeof ROLE_EVIDENCE_SCORE_SCOPE;
  sourceComposition: {
    jobDescriptionAnalysis: boolean;
    hmCalibration: boolean;
    resumeAnalysis: boolean;
    aiInterviewEvidence: boolean;
    additionalEvidenceSources: string[];
  };
  recommendation: RoleEvidenceRecommendation;
  assessmentStatus: AssessmentStatus;
  topReasons: string[];
  risks: string[];
  requiredHumanFollowUps: string[];
}

export interface RoleEvidenceAdvisoryRecommendation {
  recommendation: RoleEvidenceRecommendation;
  label: string;
  scope: typeof ROLE_EVIDENCE_SCORE_SCOPE;
  reasonFamilies: string[];
  topReasons: string[];
  risks: string[];
  requiredHumanFollowUps: string[];
  automationPolicy: {
    autoAdvanceAllowed: false;
    autoRejectAllowed: false;
    hiddenFilteringAllowed: false;
  };
}

export interface RecruiterRequirementMatrixRow {
  requirementId: string;
  title: string;
  importance: RequirementImportance;
  assessmentMode: RequirementAssessmentMode;
  answer: RecruiterRequirementAnswer;
  evidenceStrength: EvidenceStrength;
  evidenceCounts: {
    resume: number;
    interview: number;
    contradictions: number;
  };
  hasRecommendedHumanFollowUp: boolean;
}

export interface EvidenceAnchor {
  id: string;
  sourceType: EvidenceAnchorSource;
  sourceSpanId: string;
  quote?: string;
  summary: string;
  strength: EvidenceAnchorStrength;
  timestamp?: string;
}

export interface RequirementEvidenceDrilldown {
  requirementId: string;
  title: string;
  answer: RecruiterRequirementAnswer;
  evidenceStrength: EvidenceStrength;
  rationale: string;
  recommendedHumanFollowUp: string | null;
  resumeEvidence: EvidenceAnchor[];
  interviewEvidence: EvidenceAnchor[];
  contradictionEvidence: EvidenceAnchor[];
}

export interface RoleEvidenceInterviewAnalysis {
  summary: string;
  sourceAttemptId: string | null;
  generatedAt: number | null;
}

export interface RoleEvidenceDecisionLensPayload {
  schemaVersion?: string;
  generatedAt?: number;
  source?: Record<string, unknown>;
  summary?: {
    verdict?: string;
    rationale?: string;
  };
  categories?: unknown[];
}

export interface RoleEvidenceReportProjection {
  available: boolean;
  scope: typeof ROLE_EVIDENCE_SCORE_SCOPE;
  cockpit: {
    roleEvidenceDecision: RoleEvidenceDecisionPayload | null;
    advisoryRecommendation: RoleEvidenceAdvisoryRecommendation | null;
    assessmentStatus: AssessmentStatus;
    assessmentReadiness: AssessmentReadiness | null;
    topReasons: string[];
    risks: string[];
    requiredHumanFollowUps: string[];
  };
  interviewAnalysis: RoleEvidenceInterviewAnalysis | null;
  requirementMatrix: RecruiterRequirementMatrixRow[];
  evidenceDrilldowns: RequirementEvidenceDrilldown[];
  humanHandoff: {
    doNotRepeat: string[];
    focusAreas: string[];
  };
  decisionLens?: RoleEvidenceDecisionLensPayload | null;
  metadata: {
    sourceAttemptId: string | null;
    generatedAt: number | null;
  };
}

export interface RoleEvidenceOverridePayload {
  newDecision: RoleEvidenceRecommendation;
  overrideReason: string;
  note?: string | null;
}

export type RecruiterDecision = 'advance' | 'decline' | 'restore' | 'unadvance';

export type PriorRecommendation =
  | 'advance'
  | 'decline'
  | 'hold'
  | 'human_review_required';

export type DispositionReasonId =
  | 'does_not_meet_required_quals'
  | 'quals_weaker_on_review'
  | 'available_evidence_insufficient'
  | 'below_other_candidates'
  | 'level_mismatch'
  | 'hiring_constraint'
  | 'other';

export type OverrideReasonId =
  | 'evidence_weaker_than_rated'
  | 'evidence_stronger_than_rated'
  | 'role_context_missing'
  | 'ai_weighted_gap_too_heavily'
  | 'conflict_missed'
  | 'referral_external_context'
  | 'not_about_wisedoc_pipeline'
  | 'other';

export type HoldResolutionReasonId =
  | 'panel_evidence_sufficient'
  | 'external_context'
  | 'priorities_shifted'
  | 'hiring_process_constraint'
  | 'other';

export type FeedbackUse =
  | 'model_accuracy'
  | 'rubric_config'
  | 'external_context'
  | 'exclude'
  | 'needs_review';

export type CandidateDecisionSubmitRequest = {
  candidateId: string;
  decision: RecruiterDecision;
  priorRecommendation: PriorRecommendation;
  disposition?: {
    reasonId: DispositionReasonId;
  };
  overrideFeedback?: {
    reasonId: OverrideReasonId;
  };
  holdResolution?: {
    reasonId: HoldResolutionReasonId;
  };
  humanReviewDecision?: Record<string, never>;
  notes?: string;
};

export type CandidateDecisionRecord = CandidateDecisionSubmitRequest & {
  decisionId: string;
  recruiterId: string;
  timestamp: string;
  panelSnapshotId?: string;
  feedbackUse?: FeedbackUse;
  previousState?: {
    isShortlisted: boolean;
  };
  restoredState?: {
    isShortlisted: boolean;
  };
};

export interface InterviewState {
  status: InterviewStatus;
  latestAttemptId: string | null;
  latestSessionId: string | null;
  variant: InterviewVariant | null;
  launchedBy: string | null;
  launchedAt: number | null;
  startedAt: number | null;
  completedAt: number | null;
  reviewedAt: number | null;
  summary: string | null;
  recommendation: string | null;
  validatedStrengths: string[];
  unresolvedConcerns: string[];
  failureReason: string | null;
}

export interface InterviewAttemptSummary {
  attemptId: string | null;
  status: string | null;
  variant: InterviewVariant | null;
  sessionId: string | null;
  launchedBy: string | null;
  launchedAt: number | null;
  startedAt: number | null;
  completedAt: number | null;
  reviewedAt: number | null;
  summary: string | null;
  recommendation: string | null;
  validatedStrengths: string[];
  unresolvedConcerns: string[];
  failureReason: string | null;
  artifactRefs?: InterviewArtifactRefs | null;
  structuredReview: {
    segmentCoverage?: string[];
    topSkills?: string[];
    transcriptLineCount?: number | null;
  } | null;
}

export interface InterviewMediaArtifact {
  key?: string;
  url?: string;
  urlExpiresAt?: number;
  mediaKind?: 'audio' | 'video';
  contentType?: string;
  playable?: boolean;
  source?: string;
  attemptId?: string;
  sessionId?: string;
  sizeBytes?: number | null;
  durationMs?: number | null;
  uploadedAt?: number | null;
  fileName?: string | null;
}

export interface InterviewTranscriptArtifact {
  key?: string;
  url?: string;
  urlExpiresAt?: number;
  contentType?: string;
  source?: string;
  attemptId?: string;
  sessionId?: string | null;
  lineCount?: number | null;
  uploadedAt?: number | null;
}

export interface InterviewArtifactRefs {
  fullAudio?: InterviewMediaArtifact | null;
  fullVideo?: InterviewMediaArtifact | null;
  transcript?: InterviewTranscriptArtifact | null;
  [key: string]: unknown;
}

export interface InterviewDetails {
  interviewState: InterviewState;
  latestInterviewAttempt: InterviewAttemptSummary | null;
  activeLaunchSession?: LaunchApplicationInterviewData | null;
  canLaunchInterview: boolean;
  launchBlockReason: string | null;
  canSendInterviewInvite?: boolean;
  inviteBlockReason?: string | null;
  interviewInviteUrl?: string | null;
  interviewInviteExpiresAt?: number | null;
  candidateName?: string;
  jobTitle?: string;
  status?: 'closed';
}

export interface LaunchApplicationInterviewPayload {
  jobId: string;
  applicationId: string;
  variant?: InterviewVariant;
}

export interface LaunchApplicationInterviewData {
  orgId?: string;
  attemptId: string;
  sessionId: string;
  wsUrl: string;
  variant: InterviewVariant;
  totalMs: number | null;
  segments: string[];
}

export type HMCalibrationStatus =
  | 'hm_confirmed'
  | 'hm_edited'
  | 'system_proposed_hm_unconfirmed';

export type RoleVerificationCalibrationStatus =
  | HMCalibrationStatus
  | 'mixed_hm_calibration';

export interface RoleVerificationCalibrationRequirement {
  requirementId: string;
  title: string;
  importance: RequirementImportance;
  assessmentMode: RequirementAssessmentMode;
  calibrationStatus: HMCalibrationStatus;
  evidenceGoal: string;
  suggestionReason: string | null;
  sourceCriterionId: string | null;
  sourceImportance: number | null;
  sourceGatePolicy: string | null;
  linkedClaimIds: string[];
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface RoleVerificationCalibration {
  schemaVersion: 'hm_calibration_v1';
  calibrationStatus: RoleVerificationCalibrationStatus;
  requirements: RoleVerificationCalibrationRequirement[];
  hardGateEnabledRequirementIds: string[];
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface RoleVerificationCalibrationReadModel {
  calibration: RoleVerificationCalibration;
  source: 'persisted' | 'system_proposed_hm_unconfirmed';
}

export interface ResolveInterviewEntryData {
  applicationId: string;
  candidateName: string;
  candidateFullName: string;
  jobTitle: string;
  launchSession: LaunchApplicationInterviewData;
}

export interface PrepareInterviewEntryData {
  applicationId: string;
  candidateName: string;
  candidateFullName: string;
  jobTitle: string;
  mode: 'start' | 'reconnect';
  canContinue: boolean;
  activeAttemptId: string | null;
  activeAttemptStatus: string | null;
  inviteTokenExpiresAt: number | null;
}

export interface BoardBillingStatus {
  credits: number;
  hasPaymentMethod: boolean;
}

export interface SalesDashboardAccess {
  canAccess: boolean;
  role: string;
  permissions: string[];
}

export interface SalesDashboardOrganization {
  orgUuid: string;
  organisationName: string;
  organisationLogo: string;
  website: string;
  industry: string;
  companySize: string;
  createdAt: number | null;
  setupStatus: string;
  isOnboarded: boolean;
  credits: number;
  memberCount: number;
  acceptedMemberCount: number;
  pendingMemberCount: number;
}

export interface SalesDashboardOrganizationCreditsUpdate {
  orgUuid: string;
  organisationName: string;
  credits: number;
  creditsLastUpdatedAt?: number;
  creditsLastUpdatedBy?: string;
}

export interface SalesDashboardMembersResponse {
  members: TeamMember[];
}

export interface SalesDashboardJobSummary {
  jobId: string;
  displayJobId: string;
  jobTitle: string;
  jobLink: string;
  receivedApplications: number;
  status: string;
  createdAt: number | null;
  lastUpdated: number | null;
}

export interface SalesDashboardJobDetails {
  currency?: string;
  department?: string;
  displayJobId?: string;
  education?: string;
  employmentType?: string;
  jobDescription?: unknown;
  jobId?: string;
  jobLink?: string;
  jobTitle?: string;
  location?: string;
  maxPay?: string | number;
  minPay?: string | number;
  orgId?: string;
  preScreeningQuestions?: unknown;
  receivedApplications: number;
  status?: string;
  travelRequired?: string;
  voluntaryDisclosureQuestions?: unknown;
  workspaceDescription?: unknown;
  workspaceName?: string;
  yearsExp?: string;
  salaryPeriod?: string;
}

class DashboardController extends HttpClient {
  getAllApplications = async (
    orgId: string,
    jobId: string,
    token: string,
    options?: {
      lastEvaluatedKey?: Record<string, unknown> | null;
      pageCursor?: string | null;
      sortBy?: string;
      experienceBucket?: string;
      matchBucket?: string;
      stageFilter?: string;
      statusFilter?: string;
      searchTerm?: string;
    },
  ) => {
    const hasBackendFilter = Boolean(
      options?.experienceBucket ||
      options?.matchBucket ||
      options?.stageFilter ||
      options?.statusFilter ||
      options?.searchTerm,
    );
    const canPreserveSelectedSortWithBackendFilter =
      hasBackendFilter &&
      !options?.experienceBucket &&
      !options?.matchBucket &&
      !options?.stageFilter &&
      (options?.sortBy === 'newest' || options?.sortBy === 'exp-desc');
    let sortingParams: Record<string, unknown> = {};
    if (!hasBackendFilter) {
      if (options?.sortBy === 'newest' || options?.sortBy === 'exp-desc') {
        sortingParams = { sortBy: options.sortBy };
      } else {
        sortingParams = { applyAgenticShortlistMerge: true };
      }
    } else if (canPreserveSelectedSortWithBackendFilter) {
      sortingParams = { sortBy: options?.sortBy };
    }

    const response = await this.client.get(
      `/getAllApplications/${orgId}/${jobId}`,
      {
        headers: {
          Authorization: token,
        },
        params: {
          ...sortingParams,
          ...(options?.experienceBucket
            ? { experienceBucket: options.experienceBucket }
            : {}),
          ...(options?.matchBucket ? { matchBucket: options.matchBucket } : {}),
          ...(options?.stageFilter ? { stageFilter: options.stageFilter } : {}),
          ...(options?.statusFilter
            ? { statusFilter: options.statusFilter }
            : {}),
          ...(options?.searchTerm ? { searchTerm: options.searchTerm } : {}),
          ...(options?.lastEvaluatedKey
            ? {
                lastEvaluatedKey: JSON.stringify(options.lastEvaluatedKey),
              }
            : {}),
          ...(options?.pageCursor ? { pageCursor: options.pageCursor } : {}),
        },
      },
    );
    return response.data;
  };

  createJobPosting = async (
    payload: CreateJobPostingPayload,
    token: string,
  ) => {
    const response = await this.client.post('/createJobPosting', payload, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  updateJobPosting = async (
    payload: UpdateJobPostingPayload,
    token: string,
  ) => {
    const response = await this.client.put('/updateJobPosting', payload, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getAllJobPostings = async (orgId: string, token: string) => {
    const response = await this.client.get(`/getAllJobPostings/${orgId}`, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getJobPostingById = async (orgId: string, jobId: string, token: string) => {
    const response = await this.client.get(`/getJobPosting/${orgId}/${jobId}`, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  deleteJobPosting = async (orgId: string, jobId: string, token: string) => {
    const response = await this.client.delete(
      `/deleteJobPosting/${orgId}/${jobId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  addPaymentDetails = async (
    payload: { orgId: string; region: string; existingCustomerId: string },
    token: string,
  ) => {
    const response = await this.client.post('/addPaymentDetails', payload, {
      headers: {
        Authorization: token,
        region: payload.region,
      },
    });
    return response.data;
  };

  hasSavedCard = async (orgId: string, token: string) => {
    const response = await this.client.get(`/hasSavedCard/${orgId}`, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getBillingStatus = async (orgId: string, token: string) => {
    const response = await this.client.get(`/billingStatus/${orgId}`, {
      headers: {
        Authorization: token,
      },
    });
    const payload = response.data as {
      data?: { credits?: number; hasPaymentMethod?: boolean };
      credits?: number;
      hasPaymentMethod?: boolean;
    };

    return (
      payload.data ?? {
        credits:
          typeof payload.credits === 'number' &&
          Number.isFinite(payload.credits)
            ? payload.credits
            : 0,
        hasPaymentMethod: Boolean(payload.hasPaymentMethod),
      }
    );
  };

  getApplicationsSince = async (
    orgId: string,
    jobId: string,
    since: number,
    token: string,
  ) => {
    const response = await this.client.get(
      `/getApplicationsSince/${orgId}/${jobId}`,
      {
        headers: {
          Authorization: token,
        },
        params: {
          since,
          applyAgenticShortlistMerge: true,
        },
      },
    );
    return response.data;
  };

  getApplicationDetails = async (
    jobId: string,
    applicationId: string,
    token: string,
  ) => {
    const response = await this.client.get(
      `/getApplicationDetails/${jobId}/${applicationId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  updateApplicationById = async (
    jobId: string,
    applicantId: string,
    payload: object,
    token: string,
  ) => {
    const response = await this.client.put(
      `/updateApplication`,
      { ...payload, jobId, applicationId: applicantId },
      { headers: { Authorization: token } },
    );
    return response.data;
  };

  getInterviewDetails = async (applicationId: string, token: string) => {
    const response = await this.client.get(
      `/getInterviewDetails/${applicationId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  getRoleVerificationCalibration = async (
    jobId: string,
    applicationId: string,
    token: string,
  ) => {
    const response = await this.client.get(
      `/roleVerificationCalibration/${jobId}/${applicationId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  getRoleEvidenceReport = async (
    jobId: string,
    applicationId: string,
    token: string,
  ) => {
    const response = await this.client.get(
      `/roleEvidenceReport/${jobId}/${applicationId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  submitRoleEvidenceOverride = async (
    jobId: string,
    applicationId: string,
    payload: RoleEvidenceOverridePayload,
    token: string,
  ) => {
    const response = await this.client.post(
      `/roleEvidenceOverride/${jobId}/${applicationId}`,
      payload,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  submitCandidateDecision = async (
    jobId: string,
    applicationId: string,
    payload: CandidateDecisionSubmitRequest,
    token: string,
  ) => {
    const response = await this.client.post(
      `/candidateDecision/${jobId}/${applicationId}`,
      payload,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  updateRoleVerificationCalibration = async (
    jobId: string,
    applicationId: string,
    requirements: RoleVerificationCalibrationRequirement[],
    token: string,
  ) => {
    const response = await this.client.put(
      `/roleVerificationCalibration/${jobId}/${applicationId}`,
      { requirements },
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  launchApplicationInterview = async (
    payload: LaunchApplicationInterviewPayload,
    token: string,
  ) => {
    const response = await this.client.post(
      '/launchApplicationInterview',
      payload,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  sendInterviewInvitations = async (
    orgId: string,
    jobId: string,
    applicationIds: string[],
    token: string,
  ) => {
    const response = await this.client.post(
      `/sendInterviewInvitations`,
      { orgId, jobId, applicationIds },
      { headers: { Authorization: token } },
    );
    return response.data;
  };

  resolveInterviewEntry = async (
    applicationId: string,
    inviteToken: string,
    token?: string,
  ) => {
    const headers = token ? { Authorization: token } : undefined;
    const response = await this.client.post(
      `/resolveInterviewEntry/${applicationId}`,
      { inviteToken },
      { headers },
    );
    return response.data;
  };

  prepareInterviewEntry = async (
    applicationId: string,
    inviteToken: string,
    token?: string,
  ) => {
    const headers = token ? { Authorization: token } : undefined;
    const response = await this.client.post(
      `/prepareInterviewEntry/${applicationId}`,
      { inviteToken },
      { headers },
    );
    return response.data;
  };

  joinInterviewEntry = async (
    applicationId: string,
    inviteToken: string,
    variant?: InterviewVariant,
    token?: string,
  ) => {
    const headers = token ? { Authorization: token } : undefined;
    const response = await this.client.post(
      `/joinInterviewEntry/${applicationId}`,
      { inviteToken, ...(variant ? { variant } : {}) },
      { headers },
    );
    const maybeReturnedError = response as {
      data?: unknown;
      response?: unknown;
    };
    if (maybeReturnedError.response && maybeReturnedError.data === undefined) {
      throw response;
    }
    return response.data;
  };

  initiateInterviewRecordingUpload = async (
    applicationId: string,
    payload: {
      inviteToken: string;
      attemptId: string;
      sessionId: string;
      mediaKind: 'audio' | 'video';
      contentType: string;
    },
  ) => {
    const response = await this.client.post(
      `/interviewRecording/${applicationId}/initiate`,
      payload,
    );
    return response.data;
  };

  getInterviewRecordingPartUrl = async (
    applicationId: string,
    payload: {
      inviteToken: string;
      attemptId: string;
      sessionId: string;
      mediaKind: 'audio' | 'video';
      contentType: string;
      key: string;
      uploadId: string;
      partNumber: number;
    },
  ) => {
    const response = await this.client.post(
      `/interviewRecording/${applicationId}/part-url`,
      payload,
    );
    return response.data;
  };

  completeInterviewRecordingUpload = async (
    applicationId: string,
    payload: {
      inviteToken: string;
      attemptId: string;
      sessionId: string;
      mediaKind: 'audio' | 'video';
      contentType: string;
      key: string;
      uploadId: string;
      parts: Array<{ PartNumber: number; ETag: string }>;
      durationMs?: number;
      sizeBytes?: number;
      fileName?: string;
    },
  ) => {
    const response = await this.client.post(
      `/interviewRecording/${applicationId}/complete`,
      payload,
    );
    return response.data;
  };

  abortInterviewRecordingUpload = async (
    applicationId: string,
    payload: {
      inviteToken: string;
      attemptId: string;
      sessionId: string;
      mediaKind: 'audio' | 'video';
      contentType: string;
      key: string;
      uploadId: string;
    },
  ) => {
    const response = await this.client.post(
      `/interviewRecording/${applicationId}/abort`,
      payload,
    );
    return response.data;
  };

  getPublicJobDetails = async (jobId: string) => {
    const response = await this.client.get(`/getJobDetails/job/${jobId}`);
    return response.data;
  };

  getSalesDashboardAccess = async (token: string) => {
    const response = await this.client.get('/sales-dashboard/me', {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getSalesOrganizations = async (token: string) => {
    const response = await this.client.get('/sales-dashboard/organizations', {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  updateSalesOrganizationCredits = async (
    orgId: string,
    credits: number,
    token: string,
  ) => {
    const response = await this.client.put(
      `/sales-dashboard/organizations/${orgId}/credits`,
      { credits },
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  getSalesMembers = async (token: string) => {
    const response = await this.client.get('/sales-dashboard/members', {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getSalesOrganizationJobs = async (orgId: string, token: string) => {
    const response = await this.client.get(
      `/sales-dashboard/organizations/${orgId}/jobs`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  getSalesJobDetails = async (displayJobId: string, token: string) => {
    const response = await this.client.get(
      `/sales-dashboard/jobs/${displayJobId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  getPublicCandidateReport = async (candidateId: string) => {
    const response = await this.client.get(
      `/public/getCandidateReport/${candidateId}`,
    );
    return response.data;
  };
}

export default new DashboardController(
  process.env.REACT_APP_API_URL_ENTERPRISE,
);
